import { ReportsService } from '@treasury/domain/backoffice/services/reports/reports-service';
import { AchPaymentDraft } from '@treasury/domain/backoffice/types/ach';
import { Recordset } from '@treasury/FDL';
import { FdlRecord } from '@treasury/FDL/field';
import '@treasury/omega/components/omega-button';
import '@treasury/omega/components/omega-dialog';
import '@treasury/omega/components/omega-field';
import '@treasury/omega/components/omega-table';
import '@treasury/omega/components/progress/omega-progress';
import { AlertMixin } from '@treasury/omega/mixins';
import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import '../../components/blocking-loader';
import { coerceError } from '../containers/report-container';
import {
    getAchPaymentDraftColumns,
    getAchPaymentDraftFieldDefinitions,
} from '../data/ach-payment-draft-data';

enum CardState {
    BatchTable,
    FileContent,
}

const mixin = AlertMixin(LitElement);

@customElement('ach-file-detail-card')
export class AchFileDetailCard extends mixin {
    @property({ type: Object })
    public fileRecord?: FdlRecord;

    @property({ type: Boolean, reflect: true })
    public open = false;

    @state()
    private recordset!: Recordset<AchPaymentDraft>;

    @state()
    private loading = false;

    @state()
    private cardState: CardState = CardState.BatchTable;

    @state()
    private fileContent: string | null = null;

    @state()
    private achFileId: string | null = null;

    @state()
    private blockingLoader = false;

    async updated(_changedProperties: PropertyValues) {
        if (_changedProperties.has('fileRecord') && this.fileRecord) {
            this.loading = true;
            this.achFileId = this.fileRecord.getField('achFileId');
            try {
                const resp = await ReportsService.getAchFileDetails(this.achFileId);
                const payments = resp.achPaymentDrafts ?? [];
                this.recordset = new Recordset(getAchPaymentDraftFieldDefinitions(), payments);
                this.recordset.requestUpdate();
            } catch (e) {
                const { message, code } = coerceError(e);
                this.alert = {
                    ...this.alert,
                    visible: true,
                    type: 'error',
                    title: 'Error',
                    message,
                    code,
                };
            } finally {
                this.loading = false;
            }
        }
    }

    private async getFileContent() {
        if (this.fileContent) return this.fileContent;
        this.loading = true;
        try {
            if (!this.fileRecord) throw new Error('File record is not available');
            const fileArchiveId = this.fileRecord.getField('fileArchiveId');
            const resp = await ReportsService.getAchFileContent(fileArchiveId);
            if (resp.error) {
                throw new Error(resp.error);
            }
            this.fileContent = resp.fileContent;
        } catch (e) {
            const { message, code } = coerceError(e);
            this.alert = {
                ...this.alert,
                visible: true,
                type: 'error',
                title: 'Error',
                message,
                code,
            };
        } finally {
            this.loading = false;
        }
    }

    private async download({ detail }: { detail: { downloadType: string } }) {
        this.blockingLoader = true;
        try {
            await ReportsService.downloadAchFileDetails(this.achFileId, detail.downloadType);
        } catch (e) {
            const { message, code } = coerceError(e);
            this.alert = {
                ...this.alert,
                visible: true,
                type: 'error',
                title: 'Error',
                message,
                code,
            };
        } finally {
            this.blockingLoader = false;
        }
    }

    private resetCard() {
        this.achFileId = null;
        this.fileContent = null;
        this.cardState = CardState.BatchTable;
        this.alert = {
            ...this.alert,
            visible: false,
        };
    }

    renderBlockingLoader() {
        if (!this.blockingLoader) return nothing;
        return html`<blocking-loader></blocking-loader>`;
    }

    renderLoading() {
        if (!this.loading) return nothing;
        return html`<omega-progress card class="light-loader"></omega-progress>`;
    }

    renderSubheader() {
        if (!this.fileRecord) return nothing;
        if (this.cardState === CardState.FileContent) {
            return 'Delimited';
        }
        return `Source: ${this.fileRecord.getField('source')}`;
    }

    renderBatchTable() {
        if (this.loading || !this.recordset || this.cardState !== CardState.BatchTable)
            return nothing;
        return html`<omega-table
            .columnDefinitions=${getAchPaymentDraftColumns()}
            .recordset=${this.recordset}
        ></omega-table> `;
    }

    renderFileContent() {
        if (this.loading || this.cardState !== CardState.FileContent) return nothing;
        return html`<pre>${this.fileContent}</pre>`;
    }

    render() {
        return html`${this.renderBlockingLoader()}
            <omega-dialog
                .dialogTitle=${this.fileRecord ? this.fileRecord.getField('fileName') : ''}
                .open=${this.open}
                .headerActions=${this.cardState == CardState.BatchTable
                    ? ['custom', 'download']
                    : ['custom']}
                .downloadOptions=${['PDF', 'CSV']}
                @download=${this.download}
                @close=${() => {
                    this.resetCard();
                    this.dispatchEvent(new CustomEvent('close'));
                }}
                .headerButtonLabel=${this.cardState === CardState.FileContent
                    ? 'Back'
                    : 'View File Data'}
                @custom=${async () => {
                    this.cardState =
                        this.cardState === CardState.BatchTable
                            ? CardState.FileContent
                            : CardState.BatchTable;
                    if (this.cardState === CardState.FileContent) {
                        await this.getFileContent();
                    }
                }}
            >
                <div slot="sub-header" class="sub-header">${this.renderSubheader()}</div>
                <div slot="content">
                    <div class="dialog-content">
                        ${this.renderAlert()}${this.renderLoading()}${this.renderBatchTable()}
                        ${this.renderFileContent()}
                    </div>
                </div>
            </omega-dialog>`;
    }

    // eslint-disable-next-line @treasury/style-includes-host-display
    static get styles() {
        return css`
            pre {
                display: block;
                padding: 8.5px;
                margin: 0 0 9px;
                font-size: 12px;
                line-height: 1.42857143;
                word-break: break-all;
                word-wrap: break-word;
                color: #333333;
                background-color: #f5f5f5;
                border: 1px solid #ccc;
                border-radius: 3px;
            }

            .dialog-content {
                padding: 20px;
                min-width: 80%;
                max-width: 100%;
                min-height: 80%;
            }

            .sub-header {
                margin: 0;
                padding: 0 0 0 15px;
            }
        `;
    }
}
